import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/layout';
import { Zap, Target, Shield } from 'lucide-react';
import Button from '../components/ui/button';
import Section from '../components/ui/section';
import TestimonialsSection from '../components/sections/testimonials-section';
import StatsSection from '../components/sections/stats-section';
import SolutionsSection from '../components/sections/solutions-section';
import ArticlesSection from '../components/sections/articles-section';
import HubSpotModal from '../components/ui/hubspot-modal';
import HeroTemplateEnts from '../components/sections/HeroTemplateEnts';
import SectionBackground from '../components/sections/section-background';
import { WebPageSchema } from '../lib/utils/schema-utils';

export default function HomePage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  return (
    <>
      <Layout>
        <WebPageSchema
          page={{
            title: "Cabinet de recrutement innovant | Recruteurs.io",
            description: "Découvrez Recruteurs.io, le cabinet RH qui allie IA et expertise humaine pour des recrutements plus rapides, alignés et durables.",
            url: "https://recruteurs.io",
            lastReviewed: "2025-05-22",
            speakable: {
              cssSelector: ".hero-content"
            }
          }}
        />

        <HeroTemplateEnts 
          page="home"
          title={
            <>
              Recrutez des{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                talents
              </span>
              , qui partagent vos{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                valeurs
              </span>{" "}
              !
            </>
          }
          description="Le cabinet qui révolutionne le recrutement en combinant expertise RH, technologies innovantes. Le tout, avec un modèle financier unique."
          imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp"
          cta1={{
            label: " 📞 Parler à un expert",
            onClick: () => setIsHubSpotModalOpen(true)
          }}
          cta2={{
            label: "📅 Planifier un appel",
            href: "https://calendar.app.google/GudGybfrFPWVuk146"
          }}
        />

        {/* Benefits Section */}
        <SectionBackground page="home" section="home-benefits" className="relative">
          <div className="absolute inset-0 z-0">
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-notre-cabinet-de-recrutement.webp"
              alt="Cabinet de recrutement innovant à Marseille"
              className="w-full h-full object-cover opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-white" />
          </div>
          <Section>
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              Pourquoi choisir Recruteurs.io ?
            </h2>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                visible: { transition: { staggerChildren: 0.2 } },
              }}
            >
              {[{
                icon: <Zap className="w-16 h-16 text-primary" />,
                title: "Profils ultra qualifiés",
                description: "Des candidats alignés avec vos enjeux business.",
              }, {
                icon: <Target className="w-16 h-16 text-accent-blue" />,
                title: "Méthode éprouvée",
                description: "Structurée, mesurable, optimisée pour la conversion.",
              }, {
                icon: <Shield className="w-16 h-16 text-accent-yellow" />,
                title: "Process sécurisé",
                description: "Chaque étape validée, chaque décision accompagnée.",
              }].map((item, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
                  }}
                  className="text-center p-8 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <div className="mb-6 flex justify-center">{item.icon}</div>
                  <h3 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">{item.title}</h3>
                  <p className="text-neutral-700">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </Section>
        </SectionBackground>

        {/* Solutions Section */}
        <SolutionsSection onOpenHubSpot={() => setIsHubSpotModalOpen(true)} />
        
        <SectionBackground className="bg-gradient-to-r from-yellow-100 via-pink-100 to-blue-100">
          <Section>
            <h2 className="text-3xl font-bold text-center mb-4">🎁 Testez gratuitement notre IA recrutement</h2>
            <p className="text-center mb-6">En 3 minutes, générez votre première offre d'emploi hautement personnalisée avec <strong>PimpMyJob</strong>.</p>
            <div className="flex justify-center">
              <Button href="https://pimpmyjob.fr" className="text-lg">Essayer gratuitement</Button>
            </div>
          </Section>
        </SectionBackground>
        

        {/* Stats Section */}
        <SectionBackground page="home" section="home-stats" className="relative">
          <StatsSection
            title={<>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">Notre impact</span> en chiffres
            </>}
            subtitle="Des résultats concrets qui témoignent de notre expertise"
          />
        </SectionBackground>

        {/* Témoignages */}
        <SectionBackground page="home" section="home-testimonials" className="relative">
          <TestimonialsSection
            title="Ils nous font confiance ♥️"
            subtitle="Des entreprises convaincues par notre méthode RH"
          />
        </SectionBackground>

        {/* Articles */}
        <SectionBackground page="home" section="home-articles" className="relative">
          <ArticlesSection />
        </SectionBackground>

        <SectionBackground page="home" section="home-method" className="bg-white">
          <section className="max-w-4xl mx-auto px-[5vw] py-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              Notre méthode RH innovante
            </h2>
            <p className="text-lg text-neutral-800 mb-4">
              Depuis Marseille, où nous sommes installés depuis 2007, nous accompagnons les entreprises avec une conviction simple : <strong>le recrutement est un acte profondément humain</strong>. Et comme tout acte humain, il mérite transparence, respect et efficacité.
            </p>
            <p className="text-lg text-neutral-800 mb-4">
              Fondé par un ancien DRH en environnement dégradé, aujourd'hui dirigeant de Recruteurs.io, notre cabinet connaît les enjeux concrets des directions générales et RH. Nous avons conçu une approche unique qui allie <strong>l'expertise métier</strong>, une <strong>méthodologie éprouvée</strong> et des <strong>technologies d'avant-garde</strong>, notamment l'intelligence artificielle.
            </p>
            
           <p className="text-lg text-neutral-800 mb-4">
          Nos interventions couvrent trois piliers majeurs :
        </p>
        <ul className="list-disc list-inside mt-2 ml-2 text-neutral-700">
          <li>
            <strong>Le recrutement</strong> – via un process précis et une{" "}
            <a href="/offres" className="underline text-primary">
              facturation unique à l'embauche
            </a>
          </li>
          <li>
            <strong>L'audit et la transformation des processus RH</strong> – avec des solutions d'<strong>automatisation et d'IA</strong> au service de vos équipes
          </li>
          <li>
            <strong>L'externalisation RH & les campagnes de recrutement</strong> – pour agir vite, bien, et à votre image
          </li>
        </ul>
            
            <p className="text-lg text-neutral-800 mb-4">
              Ce qui nous différencie ? Notre capacité à <strong>conjuguer les valeurs humaines à la puissance technologique</strong>. Chez Recruteurs.io, nous ne vous promettons pas un miracle. Nous vous promettons un vrai partenaire.
            </p>
            <p className="text-lg text-neutral-800">
              👉 En savoir plus sur <a href="/pourquoi" className="underline text-primary">notre vision du recrutement</a>
            </p>
          </section>
        </SectionBackground>
        
      </Layout>

      {/* HubSpot Modal */}
      <HubSpotModal 
        isOpen={isHubSpotModalOpen}
        onClose={() => setIsHubSpotModalOpen(false)}
      />
    </>
  );
}